import type { ElementType } from "react";
import { Link } from "react-router-dom";
import { Dropdown, Spinner } from "react-bootstrap";

import type { AuthUserProps } from "src/types/auth";

interface BuilderUserMenuProps {
  userData: AuthUserProps;
  isSuperAdmin: boolean;
  toggleAs: ElementType;
  openKey: string | null;
  onOpen: (key: string) => void;
  onScheduleClose: (key: string) => void;
  onSetOpenKey: (key: string | null) => void;
  onNavigateToActiveDashboard: () => void;
  onLogout: () => void;
  isLoggingOut: boolean;
}

const DROPDOWN_KEY = "user-dropdown";

/**
 * The builder header's user dropdown. Kept separate from the analytics
 * header's UserMenu: the menu items genuinely differ (Active Dashboards
 * instead of Dashboard Builder, no User Management entry).
 */
export default function BuilderUserMenu({
  userData,
  isSuperAdmin,
  toggleAs,
  openKey,
  onOpen,
  onScheduleClose,
  onSetOpenKey,
  onNavigateToActiveDashboard,
  onLogout,
  isLoggingOut,
}: BuilderUserMenuProps) {
  return (
    <Dropdown
      as="li"
      className="nav-item"
      show={openKey === DROPDOWN_KEY}
      onMouseEnter={() => onOpen(DROPDOWN_KEY)}
      onMouseLeave={() => onScheduleClose(DROPDOWN_KEY)}
      onToggle={(nextShow) => onSetOpenKey(nextShow ? DROPDOWN_KEY : null)}
    >
      <Dropdown.Toggle id={DROPDOWN_KEY} as={toggleAs} className="nav-link">
        <i className="user-icon bi bi-person"></i>
        {[userData?.firstname, userData?.lastname]
          .filter(Boolean)
          .join(" ") || userData?.name}
      </Dropdown.Toggle>

      <Dropdown.Menu
        onMouseEnter={() => onOpen(DROPDOWN_KEY)}
        onMouseLeave={() => onScheduleClose(DROPDOWN_KEY)}
        popperConfig={{
          modifiers: [
            {
              name: "offset",
              options: { offset: [0, 12] },
            },
          ],
        }}
      >
        <Dropdown.Item onClick={onNavigateToActiveDashboard} as="button">
          Active Dashboards
        </Dropdown.Item>
        <Dropdown.Item as={Link} to="/change-password">
          Change Password
        </Dropdown.Item>
        {isSuperAdmin && (
          <>
            <Dropdown.Divider />
            <Dropdown.Header>Admin Portal</Dropdown.Header>
            <Dropdown.Item as={Link} to="/portal/dashboards">
              Dashboards
            </Dropdown.Item>
            <Dropdown.Item as={Link} to="/portal/users">
              Users
            </Dropdown.Item>
            <Dropdown.Item as={Link} to="/portal/clients">
              Clients
            </Dropdown.Item>
            <Dropdown.Item as={Link} to="/portal/programs">
              Programs
            </Dropdown.Item>
            <Dropdown.Item as={Link} to="/portal/tiers">
              Tiers
            </Dropdown.Item>
          </>
        )}
        <Dropdown.Divider />
        <Dropdown.Item className="logout-btn" as="button" onClick={onLogout} disabled={isLoggingOut}>
          {isLoggingOut ? (
            <>
              <Spinner animation="border" size="sm" className="me-1" />{" "}
              Signing out…
            </>
          ) : (
            "Logout"
          )}
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
}
